import { useCallback, useSyncExternalStore } from "react";

/**
 * Media queries for the phone and touch rules of the design system
 * (conventions/design/README.md, "Terms" and "Breakpoints"). A phone is a
 * viewport 760px wide or less (--bp-md). Touch is a coarse pointer at any
 * width. CSS modules write the same strings in their @media rules. Read them
 * from here in JS, so that the CSS and the JS change at the same width.
 */
export const PHONE_QUERY = "(max-width: 760px)";
export const TOUCH_QUERY = "(pointer: coarse)";
/** A phone, or a touch screen at any width (a tablet, a phone on its side). */
export const PHONE_OR_TOUCH_QUERY = "(pointer: coarse), (max-width: 760px)";

function getMediaQueryList(query: string): MediaQueryList | null {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return null;
  }
  return window.matchMedia(query);
}

/**
 * Returns true while `query` matches. Returns false when the window has no
 * matchMedia (a server render, or a jsdom test without a mock).
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = getMediaQueryList(query);
      if (!list) return () => {};
      // Test mocks of matchMedia can have only the older addListener API.
      if (typeof list.addEventListener === "function") {
        list.addEventListener("change", onChange);
        return () => list.removeEventListener("change", onChange);
      }
      list.addListener?.(onChange);
      return () => list.removeListener?.(onChange);
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () => getMediaQueryList(query)?.matches ?? false,
    () => false,
  );
}
